import { useEffect, useRef, useCallback } from 'react';
import { SupportTicketDto } from '../types';

const POLLING_INTERVAL = 10_000; // 10 seconds

interface UseMessagePollingOptions {
  /** Current ticket state */
  ticket: SupportTicketDto | null;
  /** Function to fetch fresh ticket data, returns the ticket or null */
  fetchTicket: () => Promise<SupportTicketDto | null>;
  /** Update ticket state with new data */
  onNewMessages: (updatedTicket: SupportTicketDto) => void;
  /** Whether polling is enabled (disable during editing, submitting, etc.) */
  enabled?: boolean;
}

/**
 * Polls for new messages in a support ticket without reloading the page.
 * Only updates state when new messages are detected (by comparing message count and last message id).
 */
export function useMessagePolling({
  ticket,
  fetchTicket,
  onNewMessages,
  enabled = true,
}: UseMessagePollingOptions) {
  const ticketRef = useRef(ticket);
  ticketRef.current = ticket;

  const poll = useCallback(async () => {
    if (!ticketRef.current) return;

    try {
      const fresh = await fetchTicket();
      if (!fresh || !ticketRef.current) return;

      const currentMessages = ticketRef.current.messages;
      const freshMessages = fresh.messages;

      // Check if messages changed: different count or different last message id
      const hasNewMessages =
        freshMessages.length !== currentMessages.length ||
        (freshMessages.length > 0 &&
          currentMessages.length > 0 &&
          freshMessages[freshMessages.length - 1].id !== currentMessages[currentMessages.length - 1].id);

      // Also check read status changes
      const readStatusChanged =
        fresh.isReadByAdmin !== ticketRef.current.isReadByAdmin ||
        fresh.isReadByUser !== ticketRef.current.isReadByUser;

      // Check status change
      const statusChanged = fresh.status !== ticketRef.current.status;

      if (hasNewMessages || readStatusChanged || statusChanged) {
        onNewMessages(fresh);
      }
    } catch {
      // Silently ignore polling errors — don't disturb the user
    }
  }, [fetchTicket, onNewMessages]);

  useEffect(() => {
    if (!enabled || !ticket) return;

    const intervalId = setInterval(poll, POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, [enabled, ticket, poll]);
}
